import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DepartmentReportSummary = () => {
  // State Management
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [deptViewMode, setDeptViewMode] = useState('card'); // 'card' or 'grid'
  const [selectedDeptEmployees, setSelectedDeptEmployees] = useState([]);
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [selectedDeptName, setSelectedDeptName] = useState('');

  // Initialize component
  useEffect(() => {
    const fetchCompanyAndData = async () => {
      const companyId = localStorage.getItem('selectedCompanyId');
      const companyName = localStorage.getItem('selectedCompanyName') || 'Your Company';

      console.log('INITIALIZING DEPARTMENT REPORT DATA:', { companyId, companyName });

      if (companyId) {
        console.log('🚀 STARTING DATA FETCH FOR COMPANY:', companyId);

        try {
          await Promise.all([
            fetchEmployees(companyId),
            fetchDepartments(companyId)
          ]);

          console.log('✅ ALL DATA FETCH COMPLETED');
        } catch (error) {
          console.error('❌ ERROR IN DATA FETCH:', error);
          setError(`Failed to load data: ${error.message}`);
        }
      } else {
        console.warn('⚠️ NO COMPANY ID FOUND IN LOCALSTORAGE');
        setError('No company selected. Please select a company first.');
      }
    };

    fetchCompanyAndData();
  }, []);

  // Fetch department stats when data is ready
  useEffect(() => {
    if (departments.length > 0 && employees.length > 0) {
      fetchDepartmentStats();
    }
  }, [departments, employees, selectedDate]);

  // Fetch Employees
  const fetchEmployees = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      console.log('¡ FETCHING EMPLOYEES FOR DEPARTMENT REPORT:', { companyId });

      const response = await axios.get(
        `https://crm-tkxn.onrender.com/api/employees/company/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
          params: {
            populate: 'department'
          }
        }
      );

      console.log('👥 EMPLOYEES API RESPONSE:', {
        status: response.status,
        employeeCount: response.data?.length || 0
      });

      // Process employee data with department information
      const processedEmployees = response.data.map((employee) => {
        return {
          ...employee,
          displayName: employee.teamMemberName || employee.name || 'Unknown Employee',
          department: employee.department
        };
      });

      setEmployees(processedEmployees);
      console.log('✅ EMPLOYEES SET:', processedEmployees.length);

    } catch (err) {
      console.error('❌ Error fetching employees:', err);
      setError(`Failed to fetch employees: ${err.response?.data?.message || err.message}`);

      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.location.href = '/login';
      }
    }
  };

  // Fetch Departments
  const fetchDepartments = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.log('⚠️ No access token found for departments');
      return;
    }

    try {
      console.log('¡ FETCHING DEPARTMENTS FOR COMPANY:', { companyId });

      const response = await axios.get(
        `https://crm-tkxn.onrender.com/api/departments/company/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        }
      );

      console.log('DEPARTMENTS API RESPONSE:', {
        status: response.status,
        departmentCount: response.data?.length || 0
      });

      const processedDepartments = response.data.map(dept => ({
        ...dept,
        name: dept.name || dept.departmentName || 'Unknown Department'
      }));

      setDepartments(processedDepartments);
      console.log('✅ DEPARTMENTS SET:', processedDepartments.length);

    } catch (err) {
      console.error('❌ Error fetching departments:', err);
      setError(`Failed to fetch departments: ${err.response?.data?.message || err.message}`);

      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch Department Statistics
  const fetchDepartmentStats = async () => {
    const token = localStorage.getItem('authToken');
    const companyId = localStorage.getItem('selectedCompanyId');

    if (!token) {
      console.log('⚠️ No access token found for department stats');
      return;
    }

    try {
      console.log('📊 CALCULATING DEPARTMENT STATS:', {
        departmentCount: departments.length,
        employeeCount: employees.length,
        selectedDate: selectedDate
      });

      // Calculate department-wise attendance statistics
      const stats = await Promise.all(
        departments
          .filter(dept => dept._id !== 'all')
          .map(async (dept) => {
            console.log('PROCESSING DEPARTMENT:', {
              id: dept._id,
              name: dept.name
            });

            // Get employees in this department (handle both single and multiple departments)
            const deptEmployees = employees.filter(emp => {
              if (Array.isArray(emp.department)) {
                return emp.department.some(d =>
                  (typeof d === 'object' ? d._id : d) === dept._id
                );
              } else {
                const empDeptId = typeof emp.department === 'object'
                  ? emp.department._id
                  : emp.department;
                return empDeptId === dept._id;
              }
            });

            console.log(`👥 EMPLOYEES IN ${dept.name}:`, {
              count: deptEmployees.length,
              employees: deptEmployees.map(e => ({
                id: e._id,
                name: e.displayName,
                department: e.department
              }))
            });

            // Calculate attendance for selected date for each employee
            let presentCount = 0;
            let absentCount = 0;
            let lateCount = 0;
            let halfDayCount = 0;

            // For each employee, try to fetch their attendance for selected date
            for (const employee of deptEmployees) {
              try {
                if (token && companyId) {
                  // Try to fetch actual attendance data
                  const attendanceResponse = await axios.get(
                    `https://crm-tkxn.onrender.com/api/attendance/employee/${employee._id}`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                      timeout: 5000,
                      params: {
                        date: selectedDate,
                        startDate: selectedDate,
                        endDate: selectedDate
                      }
                    }
                  );

                  const attendanceRecords = attendanceResponse.data || [];
                  const todayRecord = attendanceRecords.find(record => {
                    const recordDate = new Date(record.date || record.inTime).toISOString().split('T')[0];
                    return recordDate === selectedDate;
                  });

                  if (todayRecord) {
                    const status = (todayRecord.status || '').toLowerCase().trim();
                    if (status === 'present' || (todayRecord.inTime && todayRecord.outTime)) {
                      presentCount++;
                    } else if (status === 'late') {
                      lateCount++;
                      presentCount++; // Late is still considered present
                    } else if (status.includes('half')) {
                      halfDayCount++;
                      presentCount++; // Half day is considered present
                    } else if (status === 'absent' || !todayRecord.inTime) {
                      absentCount++;
                    }
                  } else {
                    // No record found for today - consider absent
                    absentCount++;
                  }
                } else {
                  // No token - use mock data or default absent
                  absentCount++;
                }
              } catch (empErr) {
                console.log(`⚠️ Could not fetch attendance for employee ${employee.displayName}:`, empErr.message);
                // Default to absent if can't fetch data
                absentCount++;
              }
            }

            const departmentStat = {
              _id: dept._id,
              name: dept.name,
              present: presentCount,
              absent: absentCount,
              late: lateCount,
              halfDay: halfDayCount,
              employees: deptEmployees.length,
              attendanceRate: deptEmployees.length > 0
                ? Math.round((presentCount / deptEmployees.length) * 100)
                : 0,
              employeeList: deptEmployees // Store employee list for modal
            };

            console.log(`📈 DEPARTMENT STATS FOR ${dept.name}:`, departmentStat);
            return departmentStat;
          })
      );

      console.log('✅ FINAL DEPARTMENT STATS:', stats);
      setDepartmentStats(stats);
    } catch (err) {
      console.error('❌ Error calculating department stats:', err);

      // Fallback: Create basic stats with employee counts
      const fallbackStats = departments
        .filter(dept => dept._id !== 'all')
        .map((dept) => {
          const employeeCount = employees.filter(emp => {
            if (Array.isArray(emp.department)) {
              return emp.department.some(d =>
                (typeof d === 'object' ? d._id : d) === dept._id
              );
            } else {
              const empDeptId = typeof emp.department === 'object'
                ? emp.department._id
                : emp.department;
              return empDeptId === dept._id;
            }
          }).length;

          return {
            _id: dept._id,
            name: dept.name,
            present: 0,
            absent: employeeCount,
            late: 0,
            halfDay: 0,
            employees: employeeCount,
            attendanceRate: 0,
            employeeList: []
          };
        });

      setDepartmentStats(fallbackStats);
      console.log('„ Using fallback department stats:', fallbackStats);
    }
  };

  // Fetch employees by department for modal
  const fetchEmployeesByDepartment = (deptId, deptName) => {
    console.log('👥 FETCHING EMPLOYEES FOR DEPARTMENT:', { deptId, deptName });

    const deptEmployees = employees.filter(emp => {
      if (Array.isArray(emp.department)) {
        return emp.department.some(d =>
          (typeof d === 'object' ? d._id : d) === deptId
        );
      } else {
        const empDeptId = typeof emp.department === 'object'
          ? emp.department._id
          : emp.department;
        return empDeptId === deptId;
      }
    });

    setSelectedDeptEmployees(deptEmployees);
    setSelectedDeptName(deptName);
    setIsDeptModalOpen(true);
  };

  // Close department modal
  const closeDeptModal = () => {
    setIsDeptModalOpen(false);
    setSelectedDeptEmployees([]);
    setSelectedDeptName('');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading department report...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <div className="text-red-600 text-lg mb-2">⚠️ Error Loading Department Report</div>
        <p className="text-red-700">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          „ Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">📊 Department Report Summary</h2>
          <p className="text-gray-600 mt-1">
            Attendance overview by department for {new Date(selectedDate).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}
          </p>
        </div>

        {/* View Toggle Buttons */}
        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setDeptViewMode('card')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${deptViewMode === 'card'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
              }`}
          >
            <span>📋 Card View</span>
          </button>
          <button
            onClick={() => setDeptViewMode('grid')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${deptViewMode === 'grid'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
              }`}
          >
            <span>📊 Grid View</span>
          </button>
        </div>
      </div>

      {/* Date Selector */}
      <div className="mb-6 bg-white rounded-lg p-4 shadow-sm border">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">… Select Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={fetchDepartmentStats}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            „ Refresh Stats
          </button>
        </div>
      </div>

      {/* Card View for Departments */}
      {deptViewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {departmentStats.length > 0 ? (
            departmentStats.map(dept => (
              <div key={dept._id} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 shadow border border-blue-100 hover:shadow-md transition-shadow">
                <h3 className="font-bold text-lg text-gray-800 mb-3 capitalize">{dept.name}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Employees:</span>
                    <span className="font-semibold">{dept.employees}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Present:</span>
                    <span className="font-semibold text-green-600">{dept.present}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Absent:</span>
                    <span className="font-semibold text-red-600">{dept.absent}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Half Day:</span>
                    <span className="font-semibold text-orange-600">{dept.halfDay || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Late Arrivals:</span>
                    <span className="font-semibold text-yellow-600">{dept.late}</span>
                  </div>
                  <button
                    onClick={() => fetchEmployeesByDepartment(dept._id, dept.name)}
                    className="mt-3 w-full bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                  >
                    View Employees
                  </button>
                  <div className="pt-2 mt-2 border-t border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Attendance Rate:</span>
                      <span className="font-semibold text-blue-600 text-lg">
                        {dept.attendanceRate || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8">
              <div className="text-gray-400 text-6xl mb-4"></div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">No Department Data</h3>
              <p className="text-gray-500 mb-4">Loading department statistics...</p>
              <button
                onClick={fetchDepartmentStats}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                „ Load Data
              </button>
            </div>
          )}
        </div>
      )}

      {/* Grid View for Departments */}
      {deptViewMode === 'grid' && (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {departmentStats.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Department
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      👥 Employees
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      ✅ Present
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      ❌ Absent
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      • Half Day
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      Late
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">
                      📊 Rate
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                      š™ï Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {departmentStats.map((dept, index) => (
                    <tr key={dept._id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                      }`}>
                      <td className="px-6 py-4 whitespace-nowrap border-r border-gray-100">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0">
                            {dept.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 capitalize">
                              {dept.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-100">
                        <span className="text-lg font-bold text-gray-800">{dept.employees}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-100">
                        <span className="text-lg font-bold text-green-600">{dept.present}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-100">
                        <span className="text-lg font-bold text-red-600">{dept.absent}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-100">
                        <span className="text-lg font-bold text-orange-600">{dept.halfDay || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-100">
                        <span className="text-lg font-bold text-yellow-600">{dept.late}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-100">
                        <span className={`text-lg font-bold ${(dept.attendanceRate || 0) >= 90 ? 'text-green-600' :
                          (dept.attendanceRate || 0) >= 75 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                          {dept.attendanceRate || 0}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => fetchEmployeesByDepartment(dept._id, dept.name)}
                          className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 text-sm font-medium"
                        >
                          👥 View Employees
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4"></div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">No Department Data</h3>
              <p className="text-gray-500 mb-4">Loading department statistics...</p>
              <button
                onClick={fetchDepartmentStats}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                „ Load Data
              </button>
            </div>
          )}
        </div>
      )}

      {/* Department Employees Modal */}
      {isDeptModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                👥 Employees in {selectedDeptName}
              </h3>
              <button
                onClick={closeDeptModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✅•
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedDeptEmployees.map(employee => (
                <div key={employee._id} className="bg-gray-50 rounded-lg p-4 border">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {employee.displayName.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">{employee.displayName}</h4>
                      <p className="text-sm text-gray-600">{employee.email || 'No email'}</p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    ž {employee.phone || employee.phoneNumber || 'No phone'}
                  </div>
                </div>
              ))}
            </div>

            {selectedDeptEmployees.length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-2">👥</div>
                <p className="text-gray-600">No employees found in this department</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentReportSummary;


